import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/AuthContext.jsx'
import { useTheme } from '../lib/useTheme.js'
import Input, { Field } from '../components/ui/Input.jsx'
import Button from '../components/ui/Button.jsx'

export default function Login() {
  const { session, loading } = useAuth()
  useTheme() // applies the visitor's saved theme preference to this unauthenticated screen too
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (!loading && session) return <Navigate to="/" replace />

  async function handleSubmit(e) {
    e.preventDefault()
    setBusy(true)
    setError('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    setBusy(false)
    if (error) setError(error.message)
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      {/* Ambient glow — quiet, not loud */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-blue/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full bg-sky/10 blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-[380px]"
      >
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue to-[#2f6fd6] flex items-center justify-center font-display font-extrabold text-white text-xl shadow-glow mb-4">
            F
          </div>
          <h1 className="font-display font-bold text-[22px]">FlipStudio</h1>
          <p className="text-muted text-[13px] mt-1">Sign in to your dashboard</p>
        </div>

        <div className="glass rounded-lg shadow-elev2 p-6">
          <form onSubmit={handleSubmit}>
            <Field label="Email">
              <Input type="email" icon={Mail} placeholder="you@agency.com" value={email}
                onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </Field>
            <Field label="Password">
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} icon={Lock} placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)} required />
                <button type="button" onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-2 hover:text-muted transition-colors"
                  aria-label={showPw ? 'Hide password' : 'Show password'}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>

            {error && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[12.5px] text-danger mb-4">
                {error}
              </motion.p>
            )}

            <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
              Sign in
              {!busy && <ArrowRight size={16} />}
            </Button>
          </form>
        </div>

        <p className="text-center text-muted-2 text-[12px] mt-6">
          Need access? Contact JLR Dev to set up your account.
        </p>
      </motion.div>
    </div>
  )
}
